<template>
  <ContentWrap>
    <el-alert
      class="mb-12px"
      type="info"
      :closable="false"
      show-icon
      title="会员在小程序企业主页底部「报告纠错」提交，平台核实后点「已采纳」并到「资源列表」修正资料；不采纳请填写说明。纠错不会自动改动企业资料。"
    />
    <el-form :inline="true" :model="query" class="-mb-15px">
      <el-form-item label="处理状态">
        <el-select v-model="query.status" clearable class="!w-140px" placeholder="全部">
          <el-option label="待处理" value="pending" />
          <el-option label="已采纳" value="adopted" />
          <el-option label="不采纳" value="rejected" />
        </el-select>
      </el-form-item>
      <el-form-item label="关键词">
        <el-input
          v-model="query.keyword"
          clearable
          class="!w-200px"
          placeholder="纠错说明 / 内容"
          @keyup.enter="search"
        />
      </el-form-item>
      <el-form-item label="资源ID">
        <el-input v-model="query.resourceId" clearable class="!w-160px" placeholder="按资源ID筛选" />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="search">搜索</el-button>
        <el-button @click="reset">重置</el-button>
      </el-form-item>
    </el-form>
  </ContentWrap>

  <ContentWrap>
    <el-table v-loading="loading" :data="list" stripe>
      <el-table-column prop="resourceName" label="被纠错资源" min-width="180" show-overflow-tooltip>
        <template #default="{ row }">{{ row.resourceName || '资源#' + row.resourceId }}</template>
      </el-table-column>
      <el-table-column prop="infoField" label="报错栏目" width="110" />
      <el-table-column prop="wrongValue" label="页面显示" min-width="140" show-overflow-tooltip>
        <template #default="{ row }">{{ row.wrongValue || '-' }}</template>
      </el-table-column>
      <el-table-column prop="rightValue" label="会员建议改为" min-width="160" show-overflow-tooltip>
        <template #default="{ row }">{{ row.rightValue || '-' }}</template>
      </el-table-column>
      <el-table-column prop="content" label="补充说明" min-width="200" show-overflow-tooltip />
      <el-table-column label="提交人" min-width="160">
        <template #default="{ row }">
          <div class="flex items-center gap-8px">
            <el-avatar :size="26" :src="row.userAvatar">
              {{ (row.userNickname || '会').slice(0, 1) }}
            </el-avatar>
            <span>{{ row.userNickname || '未命名会员' }}</span>
          </div>
        </template>
      </el-table-column>
      <el-table-column label="状态" width="90">
        <template #default="{ row }">
          <el-tag :type="statusTag(row.status)" size="small">{{ statusText(row.status) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="handleRemark" label="处理说明" min-width="140" show-overflow-tooltip>
        <template #default="{ row }">{{ row.handleRemark || '-' }}</template>
      </el-table-column>
      <el-table-column label="提交时间" width="170">
        <template #default="{ row }">{{ formatTime(row.createTime) }}</template>
      </el-table-column>
      <el-table-column label="操作" width="180" fixed="right">
        <template #default="{ row }">
          <el-button link type="primary" @click="openDetail(row)">详情</el-button>
          <template v-if="row.status === 'pending'">
            <el-button
              v-hasPermi="['bosscircle:correction:handle']"
              link
              type="success"
              @click="handleAdopt(row)"
            >
              已采纳
            </el-button>
            <el-button
              v-hasPermi="['bosscircle:correction:handle']"
              link
              type="danger"
              @click="openReject(row)"
            >
              不采纳
            </el-button>
          </template>
          <span v-else class="text-12px text-#909399">已处理</span>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:page="query.pageNo"
      v-model:limit="query.pageSize"
      :total="total"
      @pagination="load"
    />
  </ContentWrap>

  <Dialog title="纠错报告详情" v-model="detailVisible" width="680px">
    <el-descriptions v-if="current?.id" :column="2" border>
      <el-descriptions-item label="被纠错资源">
        {{ current.resourceName || '资源#' + current.resourceId }}
      </el-descriptions-item>
      <el-descriptions-item label="资源ID">{{ current.resourceId || '-' }}</el-descriptions-item>
      <el-descriptions-item label="报错栏目">{{ current.infoField || '-' }}</el-descriptions-item>
      <el-descriptions-item label="提交人">
        {{ current.userNickname || '未命名会员' }} #{{ current.userId }}
      </el-descriptions-item>
      <el-descriptions-item label="页面显示" :span="2">{{ current.wrongValue || '-' }}</el-descriptions-item>
      <el-descriptions-item label="会员建议改为" :span="2">
        {{ current.rightValue || '-' }}
      </el-descriptions-item>
      <el-descriptions-item label="补充说明" :span="2">{{ current.content || '-' }}</el-descriptions-item>
      <el-descriptions-item label="处理状态">
        <el-tag :type="statusTag(current.status)" size="small">
          {{ statusText(current.status) }}
        </el-tag>
      </el-descriptions-item>
      <el-descriptions-item label="处理说明">{{ current.handleRemark || '-' }}</el-descriptions-item>
      <el-descriptions-item label="提交时间">{{ formatTime(current.createTime) }}</el-descriptions-item>
      <el-descriptions-item label="处理时间">{{ formatTime(current.handleTime) }}</el-descriptions-item>
    </el-descriptions>
    <div v-if="current?.images?.length" class="mt-12px flex flex-wrap gap-16px">
      <div v-for="(url, index) in current.images" :key="url" class="w-200px">
        <div class="mb-8px text-14px text-#909399">佐证图片 {{ index + 1 }}</div>
        <el-image
          :src="url"
          :preview-src-list="current.images"
          :initial-index="index"
          preview-teleported
          fit="contain"
          class="h-140px w-200px border border-solid border-[var(--el-border-color)]"
        />
      </div>
    </div>
    <template #footer>
      <el-button @click="detailVisible = false">关 闭</el-button>
    </template>
  </Dialog>

  <Dialog title="不采纳纠错报告" v-model="rejectVisible" width="500px">
    <el-form ref="rejectFormRef" :model="rejectForm" :rules="rejectRules" label-width="90px">
      <el-form-item label="资源">{{ current?.resourceName || '资源#' + current?.resourceId }}</el-form-item>
      <el-form-item label="纠错说明">{{ current?.content }}</el-form-item>
      <el-form-item label="处理说明" prop="handleRemark">
        <el-input
          v-model="rejectForm.handleRemark"
          type="textarea"
          :rows="3"
          placeholder="请说明不采纳的原因，会员可在「我的纠错」里看到"
        />
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button @click="rejectVisible = false">取 消</el-button>
      <el-button :loading="rejecting" type="danger" @click="submitReject">确定不采纳</el-button>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { formatDate } from '@/utils/formatTime'
import * as Api from '@/api/business/resource'
import type { FormInstance, FormRules } from 'element-plus'

defineOptions({ name: 'BossCircleResourceCorrection' })

const route = useRoute()
const message = useMessage()

const loading = ref(false)
const list = ref<Api.ResourceCorrectionVO[]>([])
const total = ref(0)
const query = reactive({
  pageNo: 1,
  pageSize: 10,
  status: (route.query.status as Api.ResourceCorrectionStatus) || undefined,
  keyword: '',
  resourceId: (route.query.resourceId as string) || ''
})

const statusText = (s?: string) =>
  ({ pending: '待处理', adopted: '已采纳', rejected: '不采纳' } as Record<string, string>)[s ?? ''] ||
  '-'
const statusTag = (s?: string) => (s === 'adopted' ? 'success' : s === 'rejected' ? 'info' : 'warning')

const formatTime = (value?: string | number) => (value ? formatDate(value) : '-')

const load = async () => {
  loading.value = true
  try {
    const params: any = { pageNo: query.pageNo, pageSize: query.pageSize }
    if (query.status) params.status = query.status
    if (query.keyword) params.keyword = query.keyword
    if (query.resourceId) params.resourceId = query.resourceId
    const data = await Api.getResourceCorrectionPage(params)
    list.value = data.list || []
    total.value = data.total || 0
  } finally {
    loading.value = false
  }
}

const search = () => {
  query.pageNo = 1
  load()
}

const reset = () => {
  query.status = undefined
  query.keyword = ''
  query.resourceId = ''
  search()
}

const handleAdopt = async (row: Api.ResourceCorrectionVO) => {
  await message.confirm(
    `确认采纳「${row.resourceName || '该资源'}」的这条纠错吗？采纳后请再到「资源列表」修改企业资料。`
  )
  await Api.handleResourceCorrection({ id: row.id!, adopted: true })
  message.success('已标记采纳')
  detailVisible.value = false
  load()
}

const detailVisible = ref(false)
const current = ref<Api.ResourceCorrectionVO>()

const openDetail = (row: Api.ResourceCorrectionVO) => {
  current.value = row
  detailVisible.value = true
}

const rejectVisible = ref(false)
const rejecting = ref(false)
const rejectFormRef = ref<FormInstance>()
const rejectForm = reactive<{ handleRemark: string }>({ handleRemark: '' })
const rejectRules = {
  handleRemark: [{ required: true, message: '处理说明不能为空', trigger: 'blur' }]
} as FormRules

const openReject = (row: Api.ResourceCorrectionVO) => {
  current.value = row
  rejectForm.handleRemark = ''
  rejectVisible.value = true
  nextTick(() => rejectFormRef.value?.clearValidate())
}

const submitReject = async () => {
  await rejectFormRef.value?.validate()
  rejecting.value = true
  try {
    await Api.handleResourceCorrection({
      id: current.value!.id!,
      adopted: false,
      handleRemark: rejectForm.handleRemark.trim()
    })
    message.success('已标记不采纳')
    rejectVisible.value = false
    load()
  } finally {
    rejecting.value = false
  }
}

onMounted(load)
</script>
